import { Injectable } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';

const STORAGE_KEY = 'dtr_tab_id';

/**
 * Identifies the current BROWSER TAB, not the user and not the browser
 * profile. Generated once and kept in sessionStorage specifically because,
 * unlike localStorage, sessionStorage is never shared between tabs — each
 * tab gets its own copy, so this id never leaks into a different tab of the
 * same profile.
 *
 * This is the missing half of the refinery Edit-lock identity: the
 * dtr_session cookie is shared by every tab of the same browser profile (see
 * AuthService), so it alone can't tell two tabs of the same logged-in user
 * apart. The lock is keyed on (UserId, TabId) together — see
 * RefineryPresenceService — so the same user can hold Edit in one tab and
 * Read-Only in another simultaneously.
 */
@Injectable({ providedIn: 'root' })
export class TabIdentityService {
  private readonly _tabId: string;

  constructor() {
    let id: string | null = null;
    try {
      id = sessionStorage.getItem(STORAGE_KEY);
    } catch {
      id = null;
    }
    if (!id) {
      id = uuidv4();
      try {
        sessionStorage.setItem(STORAGE_KEY, id);
      } catch {
        // Storage unavailable (private browsing, quota, etc) — fall back to
        // an in-memory id for this page's lifetime. Worst case, a reload
        // gets a new tab id and simply has to re-request Edit mode.
      }
    }
    this._tabId = id;
  }

  get tabId(): string {
    return this._tabId;
  }
}
